'use client';

import { useState } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { LocaleLink } from '@/core/i18n';
import { usePageContent } from '@/core/i18n/content';
import { PRICING_SECTION_CONTENT, type PricingSectionContent } from '@/core/i18n/content/pricing-section.content';
import type { PricingPlan } from '../types/hotels.types';
import { PRICING_PLANS } from './hotels.data';

/* -------------------------------------------------------------------------- */
/*  Currency Options                                                          */
/* -------------------------------------------------------------------------- */

const CURRENCY_OPTIONS = [
  { code: 'USD', label: 'USD ($)', flag: 'US' },
  { code: 'EUR', label: 'EUR (\u20AC)', flag: 'EU' },
  { code: 'GBP', label: 'GBP (\u00A3)', flag: 'GB' },
  { code: 'AED', label: 'AED', flag: 'AE' },
  { code: 'INR', label: 'INR (\u20B9)', flag: 'IN' },
  { code: 'THB', label: 'THB (\u0E3F)', flag: 'TH' },
] as const;

/* -------------------------------------------------------------------------- */
/*  PricingCard                                                               */
/* -------------------------------------------------------------------------- */

function PricingCard({
  plan,
  index,
  currency,
  t,
}: {
  plan: PricingPlan;
  index: number;
  currency: string;
  t: PricingSectionContent;
}) {
  const isPopular = plan.popular;
  const regional = plan.regionalPricing?.[currency];
  const displaySymbol = regional?.symbol ?? '$';
  const displayAmount = regional?.amount ?? plan.price;

  return (
    <div
      id={`hotels-pricing-card-${index}`}
      className={cn(
        'relative flex flex-col rounded-xl border bg-hms-card p-8',
        'transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-black/20',
        isPopular
          ? 'border-hms-gold shadow-md shadow-hms-gold/10'
          : 'border-hms-border hover:border-hms-gold/20',
      )}
    >
      {/* Popular badge */}
      {isPopular && (
        <span
          className={cn(
            'absolute -top-3.5 left-1/2 -translate-x-1/2',
            'rounded-full bg-hms-gold px-4 py-1 text-xs font-bold tracking-wide text-hms-bg',
          )}
        >
          {t.mostPopular}
        </span>
      )}

      {/* Plan name */}
      <h3 className="text-lg font-bold text-hms-text">{t.plans[index]?.name ?? plan.name}</h3>
      <p className="mt-1 text-sm text-hms-textSub">{t.plans[index]?.tagline ?? plan.tagline}</p>

      {/* Price */}
      <div className="mt-6 flex items-baseline gap-1">
        <span className="text-sm font-medium text-hms-textDim">{displaySymbol}</span>
        <span className="text-4xl font-extrabold tracking-tight text-hms-text">
          {displayAmount}
        </span>
        <span className="text-sm text-hms-textDim">{t.perMonth}</span>
      </div>

      {/* Divider */}
      <div className="my-6 h-px w-full bg-hms-border" />

      {/* Feature list */}
      <ul className="flex-1 space-y-3">
        {(t.plans[index]?.features ?? plan.features).map((feature) => (
          <li key={feature} className="flex items-start gap-3">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-hms-green" />
            <span className="text-sm leading-snug text-hms-textSub">
              {feature}
            </span>
          </li>
        ))}
      </ul>

      {/* CTA button */}
      <LocaleLink
        id={`hotels-pricing-cta-${index}`}
        href="/register-hotel"
        className={cn(
          'mt-8 flex items-center justify-center rounded-lg px-6 py-3',
          'text-sm font-semibold tracking-wide transition-all duration-200',
          isPopular
            ? 'bg-hms-gold text-hms-bg hover:bg-hms-gold/90'
            : 'border border-hms-border text-hms-text hover:border-hms-gold/40 hover:text-hms-gold',
        )}
      >
        {t.getStarted}
      </LocaleLink>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  PricingSection                                                            */
/* -------------------------------------------------------------------------- */

export function PricingSection() {
  const [currency, setCurrency] = useState('USD');
  const t = usePageContent(PRICING_SECTION_CONTENT);

  return (
    <section id="hotels-pricing-section" className="relative w-full overflow-hidden bg-hms-bg py-20 sm:py-28">
      {/* Subtle radial glow */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 60% 40% at 50% 30%, rgba(200,145,58,0.04) 0%, transparent 70%)',
        }}
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section heading */}
        <div className="mx-auto max-w-2xl text-center">
          <h2
            className={cn(
              'text-3xl font-extrabold tracking-tight sm:text-4xl',
              'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400',
              'bg-clip-text text-transparent',
            )}
          >
            {t.title}
          </h2>
          <p className="mt-4 text-base text-hms-textSub sm:text-lg">
            {t.subtitle}
          </p>
        </div>

        {/* Currency selector */}
        <div className="mx-auto mt-8 flex items-center justify-center gap-2">
          {CURRENCY_OPTIONS.map((opt) => (
            <button
              key={opt.code}
              id={`hotels-pricing-currency-${opt.code.toLowerCase()}`}
              type="button"
              onClick={() => setCurrency(opt.code)}
              className={cn(
                'rounded-lg px-3 py-1.5 text-xs font-medium transition-all duration-200',
                currency === opt.code
                  ? 'bg-hms-gold text-hms-bg shadow-sm shadow-hms-gold/20'
                  : 'border border-hms-border text-hms-textDim hover:border-hms-gold/30 hover:text-hms-textSub',
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Pricing grid */}
        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
          {PRICING_PLANS.map((plan, idx) => (
            <PricingCard key={plan.name} plan={plan} index={idx} currency={currency} t={t} />
          ))}
        </div>
      </div>
    </section>
  );
}
